/**
 * Product Hero V3 - Core JavaScript
 * Handles variant selection, form submission, swiper initialization, and UI updates
 * Last Updated: January 2025
 */

/* ═══════════════════════════════════════════════════════════════════════════
   SCROLL TO REVIEWS
   ═══════════════════════════════════════════════════════════════════════════ */

window.scrollToReviews = window.scrollToReviews || function () {
  const el = document.querySelector(
    '.jdgm-rev-widg__body, .jdgm-rev-widg__reviews, .jdgm-rev-widg, .jdgm-widget'
  );
  if (!el) return;
  const offset = el.getBoundingClientRect().top + window.pageYOffset - 100;
  window.scrollTo({ top: offset, behavior: 'smooth' });
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN INITIALIZATION
   ═══════════════════════════════════════════════════════════════════════════ */

document.addEventListener('DOMContentLoaded', () => {
  console.log('🚀 Product Hero V3 JS loaded');

  /* ───────────────────────── Glassmorphism Shimmer ───────────────────────── */
  
  function setupShimmerOnView() {
    const buttons = document.querySelectorAll('.hero-cta-button');
    if (!buttons.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const button = entry.target;
          button.classList.add('shimmer');
          setTimeout(() => button.classList.remove('shimmer'), 600);
          observer.unobserve(button);
        }
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -20px 0px' });

    buttons.forEach(button => observer.observe(button));
  }

  /* ───────────────────────── Swiper Initialization ───────────────────────── */
  
  (function initHeroSwiper(){
    const wrap = document.querySelector('.hero-swiper');
    if (!wrap) return;
    if (typeof Swiper === 'undefined') return setTimeout(initHeroSwiper, 200);
    
    new Swiper('.hero-swiper', {
      loop: false, 
      slidesPerView: 1, 
      spaceBetween: 8,
      navigation: { 
        nextEl: '.swiper-button-next', 
        prevEl: '.swiper-button-prev' 
      },
      pagination: { 
        el: '.swiper-pagination', 
        clickable: true 
      },
      breakpoints: { 
        768: { loop: true } 
      }
    });
  })();

  /* ───────────────────────── Variant Handling ───────────────────────── */
  
  function updateVariant() {
    const selectedOptions = [];
    const variantInputs = document.querySelectorAll('.variant-option input:checked');
    
    variantInputs.forEach(input => {
      const optionPosition = parseInt(input.dataset.optionPosition);
      selectedOptions[optionPosition - 1] = input.value;
    });
    
    const matchingVariant = findVariantByOptions(selectedOptions);
    
    if (matchingVariant) {
      const idInput = document.getElementById('hero-variant-id');
      if (idInput) idInput.value = matchingVariant.id;
      
      updateStockStatus(matchingVariant.id);
      updateVariantPrice(matchingVariant);
    }
  }

  function findVariantByOptions(selectedOptions) {
    if (typeof window.productVariants === 'undefined' || !window.currentProduct?.variants) {
      return null;
    }
    
    for (const variant of window.currentProduct.variants) {
      let isMatch = true;
      
      for (let i = 0; i < selectedOptions.length; i++) {
        if (selectedOptions[i] && selectedOptions[i] !== variant[`option${i + 1}`]) {
          isMatch = false;
          break;
        }
      }
      
      if (isMatch) {
        return window.productVariants[variant.id.toString()];
      }
    }
    
    return null;
  }

  function updateVariantPrice(variant) {
    if (!variant) return;
    
    const price = (variant.price / 100).toFixed(2);
    const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
    const comparePrice = hasComparePrice ? (variant.compare_at_price / 100).toFixed(2) : null;
    
    const ctaButton = document.getElementById('hero-cta-button');
    if (ctaButton && variant.available) {
      ctaButton.innerHTML = hasComparePrice
        ? `<span class="cta-text">Add to Cart</span>
           <span class="cta-separator">·</span>
           <span class="cta-price-compare"><del aria-label="Original price">$${comparePrice}</del></span>
           <span class="cta-price" aria-label="Sale price">$${price}</span>`
        : `<span class="cta-text">Add to Cart</span>
           <span class="cta-separator">·</span>
           <span class="cta-price">$${price}</span>`;
    }
    
    // Update one-time purchase option
    const oneTimePrice = document.querySelector('.onetime-label .option-price');
    if (oneTimePrice) {
      oneTimePrice.innerHTML = hasComparePrice
        ? `<del class="compare-price-strike" aria-label="Original price">$${comparePrice}</del> <span aria-label="Sale price">$${price}</span>`
        : `$${price}`;
    }
    
    // Update sale badge
    const saleBadge = document.querySelector('.hero-sale-badge');
    if (saleBadge) {
      saleBadge.style.display = hasComparePrice ? 'inline-block' : 'none';
    }
    
    updateVariantDisplay();
    
    // Dispatch event for floating CTA / other integrations
    document.dispatchEvent(new CustomEvent('product:price-updated', {
      detail: { 
        price: variant.price / 100, 
        compareAtPrice: variant.compare_at_price ? variant.compare_at_price / 100 : null,
        variant
      }
    }));
  }

  function updateVariantDisplay() {
    const variantDisplay = document.querySelector('.hero-variant-display');
    if (!variantDisplay) return;
    
    const selectedOptions = [];
    document.querySelectorAll('.variant-option input:checked').forEach(input => {
      selectedOptions.push(input.value.toLowerCase());
    });
    
    if (selectedOptions.length > 0) {
      variantDisplay.textContent = `(${selectedOptions.join('/')})`;
      variantDisplay.style.display = 'block';
    } else {
      variantDisplay.style.display = 'none';
    }
  }
  
  function updateStockStatus(variantId) {
    const variant = window.productVariants?.[variantId];
    if (!variant) return;
    
    const ctaButton = document.getElementById('hero-cta-button');
    const stockMessage = document.getElementById('hero-stock-message');
    
    const price = (variant.price / 100).toFixed(2);
    const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
    const comparePrice = hasComparePrice ? (variant.compare_at_price / 100).toFixed(2) : null;
    
    if (variant.available) {
      ctaButton.disabled = false;
      ctaButton.classList.remove('out-of-stock');
      
      ctaButton.innerHTML = hasComparePrice
        ? `<span class="cta-text">Add to Cart</span>
           <span class="cta-separator">·</span>
           <span class="cta-price-compare"><del aria-label="Original price">$${comparePrice}</del></span>
           <span class="cta-price" aria-label="Sale price">$${price}</span>`
        : `<span class="cta-text">Add to Cart</span>
           <span class="cta-separator">·</span>
           <span class="cta-price">$${price}</span>`;
      
      if (stockMessage) stockMessage.style.display = 'none';
    } else {
      ctaButton.disabled = true;
      ctaButton.classList.add('out-of-stock');
      ctaButton.innerHTML = `<span class="cta-text">Out of Stock</span>`;
      if (stockMessage) stockMessage.style.display = 'none';
    }
  }

  /* ───────────────────────── Form Submission ───────────────────────── */
  
  const form = document.getElementById('hero-product-form');
  if (form) {
    form.addEventListener('submit', async (e) => {
      console.log('🛒 Form submit triggered');
      
      const ctaButton = document.getElementById('hero-cta-button');
      if (ctaButton.disabled || ctaButton.classList.contains('out-of-stock')) {
        e.preventDefault();
        alert('This product is currently out of stock.');
        return;
      }
      
      // Check if subscription is selected with selling plan - let Loop handle it
      const subscriptionSelected = form.querySelector('input[name="purchase-option"][value="subscribe"]:checked');
      const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
      console.log('🔄 Subscription selected:', !!subscriptionSelected, 'Selling plan:', sellingPlanInput?.value);
      
      if (subscriptionSelected && sellingPlanInput?.value) {
        console.log('⏭️ Letting Loop handle submission');
        return; // Let form submit normally for Loop
      }
      
      e.preventDefault();
      console.log('🛒 Handling add to cart');

      const variantId = form.querySelector('input[name="id"]').value;
      const quantity = form.querySelector('input[name="quantity"]')?.value || 1;
      const item = { id: variantId, quantity: parseInt(quantity) };
      console.log('📦 Adding variant:', variantId, 'qty:', quantity);

      try {
        // Use theme's cart method if available (handles drawer refresh properly)
        if (typeof theme !== 'undefined' && theme.cart && theme.drawer) {
          console.log('🎯 Using theme.cart.addItem');
          await theme.cart.addItem({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [item] })
          }, variantId);
          theme.drawer.open('right', 'cart-drawer');

          // Refresh shipping threshold after theme adds item
          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();
          window.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: cart }));

          // Refresh cart drawer HTML for shipping threshold
          try {
            const cartDrawer = document.querySelector('cart-root.cart--root[data-view="drawer"]');
            if (cartDrawer) {
              cartDrawer.setAttribute('data-cart-total', cart.total_price);
              cartDrawer.setAttribute('data-has-items', cart.item_count > 0 ? 'true' : 'false');

              const cartPageRes = await fetch('/cart?view=ajax');
              if (cartPageRes.ok) {
                const cartHtml = await cartPageRes.text();
                const temp = document.createElement('div');
                temp.innerHTML = cartHtml;
                const newCartRoot = temp.querySelector('cart-root') || temp.querySelector('.cart--root');
                if (newCartRoot) {
                  cartDrawer.innerHTML = newCartRoot.innerHTML;
                }
              }
              window.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: cart }));
            }
          } catch (e) {
            console.log('Cart drawer refresh error:', e);
          }

          // Try theme Cart object for additional refresh
          if (typeof Cart !== 'undefined') {
            try {
              if (typeof Cart.fetchTotals === 'function') await Cart.fetchTotals();
              if (typeof Cart.updateAllHtml === 'function') await Cart.updateAllHtml(true);
            } catch (e) {}
          }

          // Final shipping threshold update
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: cart }));
          }, 100);
        } else {
          // Fallback to fetch
          console.log('🔄 Using fetch fallback');
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: [item] })
          });

          if (response.ok) {
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();

            // Update cart count elements
            document.querySelectorAll('[data-cart-count]').forEach(el => el.setAttribute('data-cart-count', cart.item_count));
            document.querySelectorAll('.cart-count, .cart-count-bubble, [data-cart-item-count]').forEach(el => el.textContent = cart.item_count);

            // Dispatch cart update events (including theme:cart:updated for shipping threshold)
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            window.dispatchEvent(new CustomEvent('cart:refresh'));
            window.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: cart }));

            if (window.Shopify?.onCartUpdate) window.Shopify.onCartUpdate(cart);

            // Try to refresh cart drawer HTML for shipping threshold
            try {
              const cartDrawer = document.querySelector('cart-root.cart--root[data-view="drawer"]');
              if (cartDrawer) {
                cartDrawer.setAttribute('data-cart-total', cart.total_price);
                cartDrawer.setAttribute('data-has-items', cart.item_count > 0 ? 'true' : 'false');

                const cartPageRes = await fetch('/cart?view=ajax');
                if (cartPageRes.ok) {
                  const cartHtml = await cartPageRes.text();
                  const temp = document.createElement('div');
                  temp.innerHTML = cartHtml;
                  const newCartRoot = temp.querySelector('cart-root') || temp.querySelector('.cart--root');
                  if (newCartRoot) {
                    cartDrawer.innerHTML = newCartRoot.innerHTML;
                  }
                }
                window.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: cart }));
              }
            } catch (e) {
              console.log('Cart drawer refresh error:', e);
            }

            // Try theme Cart object for additional refresh
            if (typeof Cart !== 'undefined') {
              try {
                if (typeof Cart.fetchTotals === 'function') await Cart.fetchTotals();
                if (typeof Cart.updateAllHtml === 'function') await Cart.updateAllHtml(true);
              } catch (e) {}
            }

            // Try to open cart drawer
            const drawerOpened =
              (window.theme?.drawer && (theme.drawer.open('right','cart-drawer'), true)) ||
              (typeof window.openCartDrawer === 'function' && (window.openCartDrawer(), true)) ||
              (document.querySelector('[data-cart-drawer]')?.classList.add('is-open'), document.querySelector('[data-cart-drawer]')) ||
              (window.ajaxCart?.open && (window.ajaxCart.open(), true)) ||
              (window.theme?.openCartDrawer && (window.theme.openCartDrawer(), true));

            // Final shipping threshold update after drawer opens
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: cart }));
            }, 100);

            if (!drawerOpened) {
              setTimeout(() => { window.location.href = '/cart'; }, 500);
            }
          } else {
            const errorData = await response.json();
            if (errorData.description?.includes('out of stock')) {
              alert('Sorry, this item is now out of stock.');
              window.location.reload();
            } else {
              alert('Sorry, something went wrong.');
            }
          }
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        alert('Sorry, something went wrong.');
      }
    });
  }

  /* ───────────────────────── Subscription Toggle (Unified) ───────────────────────── */
  
  const subscriptionToggle = document.getElementById('subscription-toggle');
  const subscriptionContainer = document.getElementById('subscription-options-container');
  
  console.log('🔄 Subscription toggle element:', subscriptionToggle);
  console.log('📦 Subscription container element:', subscriptionContainer);
  console.log('📦 Container classes:', subscriptionContainer?.classList?.toString());
  
  if (subscriptionToggle && subscriptionContainer) {
    subscriptionToggle.addEventListener('click', () => {
      console.log('🖱️ Toggle clicked! Current classes:', subscriptionContainer.classList.toString());
      subscriptionContainer.classList.toggle('expanded');
      console.log('🖱️ After toggle:', subscriptionContainer.classList.toString());
    });
  } else {
    console.log('⚠️ Subscription toggle or container not found');
  }

  /* ───────────────────────── Initialization ───────────────────────── */
  
  setupShimmerOnView();

  // Initial variant check with delays for Loop integration
  setTimeout(() => {
    updateVariant();
    setTimeout(() => {
      updateVariant();
      const firstChecked = document.querySelector('.variant-option input:checked');
      if (firstChecked) {
        firstChecked.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 500);
  }, 100);

  // Listen for variant changes
  document.addEventListener('change', (e) => {
    if (e.target.matches('.variant-option input')) {
      updateVariant();
      updateVariantDisplay();
    }
  });

  // Initial stock check
  setTimeout(() => {
    const hiddenVariantInput = document.getElementById('hero-variant-id');
    if (hiddenVariantInput?.value) {
      updateStockStatus(hiddenVariantInput.value);
      updateVariantDisplay();
    }
  }, 200);

  /* ───────────────────────── Floating CTA Bar ───────────────────────── */
  
  const floatingCTA = document.getElementById('floatingCTA');
  
  if (floatingCTA) {
    const mainCTA = document.querySelector('.hero-cta-button');
    const floatingButton = document.getElementById('hero-floating-cta-button');
    const hideSelectors = floatingCTA.dataset.hideSelectors || '';
    const scrollOffset = parseInt(floatingCTA.dataset.scrollOffset) || 50;
    
    let isCartDrawerOpen = false;
    
    // Check if any hide selectors are visible in viewport
    function isHideSelectorVisible() {
      if (!hideSelectors.trim()) return false;
      
      const selectors = hideSelectors.split(',').map(s => s.trim()).filter(s => s);
      
      for (const selector of selectors) {
        const el = document.querySelector(selector);
        if (el) {
          const rect = el.getBoundingClientRect();
          const windowHeight = window.innerHeight || document.documentElement.clientHeight;
          // Check if element is in viewport
          if (rect.top < windowHeight && rect.bottom > 0) {
            return true;
          }
        }
      }
      return false;
    }
    
    // Check if cart drawer is open
    function checkCartDrawerOpen() {
      const cartDrawer = document.querySelector('.drawer--root.is-open, [data-cart-drawer].is-open, .cart-drawer.is-open');
      return !!cartDrawer;
    }
    
    // Handle floating CTA visibility
    function handleFloatingVisibility() {
      if (!mainCTA) return;
      
      // Hide if cart drawer is open
      if (checkCartDrawerOpen()) {
        floatingCTA.classList.remove('visible');
        return;
      }
      
      // Hide if any custom selectors are visible
      if (isHideSelectorVisible()) {
        floatingCTA.classList.remove('visible');
        return;
      }
      
      // Show if scrolled past main CTA
      const rect = mainCTA.getBoundingClientRect();
      const shouldShow = rect.bottom + scrollOffset < 0;
      
      if (shouldShow) {
        if (!floatingCTA.classList.contains('visible')) {
          floatingCTA.classList.add('visible');
          // Trigger shimmer on first appearance
          setTimeout(() => {
            floatingButton.classList.add('shimmer');
            setTimeout(() => floatingButton.classList.remove('shimmer'), 600);
          }, 400);
        }
      } else {
        floatingCTA.classList.remove('visible');
      }
    }
    
    // Update floating button price/availability
    function updateFloatingButton(variant) {
      if (!variant) return;
      
      const price = (variant.price / 100).toFixed(2);
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      const comparePrice = hasCompare ? (variant.compare_at_price / 100).toFixed(2) : null;
      
      // Update floating price display (left side)
      const floatingPrice = document.getElementById('floating-cta-price');
      if (floatingPrice) {
        if (hasCompare) {
          floatingPrice.innerHTML = `
            <span class="hero-floating-cta__price-compare"><del>$${comparePrice}</del></span>
            <span class="hero-floating-cta__price-current">$${price}</span>
          `;
        } else {
          floatingPrice.innerHTML = `<span class="hero-floating-cta__price-current">$${price}</span>`;
        }
      }
      
      // Update button state
      if (floatingButton) {
        if (variant.available) {
          floatingButton.disabled = false;
          floatingButton.innerHTML = '<span class="cta-text">Add to Cart</span>';
        } else {
          floatingButton.disabled = true;
          floatingButton.innerHTML = '<span class="cta-text">Out of Stock</span>';
        }
      }
    }
    
    // Floating button click - trigger main form submission
    floatingButton.addEventListener('click', () => {
      if (floatingButton.disabled) return;
      
      const mainForm = document.getElementById('hero-product-form');
      if (mainForm) {
        // Scroll to top smoothly first
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Submit form after scroll
        setTimeout(() => {
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          mainForm.dispatchEvent(submitEvent);
        }, 300);
      }
    });
    
    // Listen for price updates from main section
    document.addEventListener('product:price-updated', (e) => {
      if (e.detail?.variant) {
        updateFloatingButton(e.detail.variant);
      }
    });
    
    // Listen for cart drawer state changes
    const drawerObserver = new MutationObserver(() => {
      handleFloatingVisibility();
    });
    
    const drawerRoot = document.querySelector('.drawer--root, [data-cart-drawer], .cart-drawer');
    if (drawerRoot) {
      drawerObserver.observe(drawerRoot, { attributes: true, attributeFilter: ['class'] });
    }
    
    // Throttled scroll handler
    let scrollTicking = false;
    window.addEventListener('scroll', () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          handleFloatingVisibility();
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    }, { passive: true });
    
    // Initial check
    handleFloatingVisibility();
    
    console.log('📱 Floating CTA initialized');
  }
});
